import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../../api/admin/users';
import { rolesApi, userRolesApi, permissionOverridesApi } from '../../../api/admin/roles';
import { approvalLimitsApi } from '../../../api/admin/approvalLimits';
import { ApiError } from '../../../api/client';
import { IconActionButton } from '../../../components/IconActionButton';
import { Trash2, Pencil } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { SectionHeader } from '../../../components/SectionHeader';
import { DataTable } from '../../../components/DataTable';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { PermissionScope, ApprovalLimit } from '../../../types/admin';
import { MODULE_LABELS } from '../../../config/module-labels';
import { genericDocumentApi } from '../../../api/generic-documents';
import { CommentsThread } from '../../../components/CommentsThread';
import { DocumentsSection } from '../../../components/DocumentsSection';
import { AuditHistoryTab } from '../../../components/AuditHistoryTab';

const scopes: PermissionScope[] = ['own', 'project', 'department', 'location', 'country', 'all'];

/** Plain-language explanations - never shown as raw enum values to a non-technical admin. */
const SCOPE_LABELS: Record<PermissionScope, string> = {
  own: 'Only their own records',
  project: 'Records within a specific project',
  department: 'Records within their department',
  location: 'Records within a specific location',
  country: 'All records in the country',
  all: 'All records, everywhere',
};

const overrideFormSchema = z.object({
  permissionKey: z.string().min(3, 'Enter a permission key, e.g. admin.departments.manage'),
  scope: z.enum(['own', 'project', 'department', 'location', 'country', 'all']),
  isGranted: z.enum(['true', 'false']),
});
type OverrideFormValues = z.infer<typeof overrideFormSchema>;

const combinedFormSchema = z.object({
  roleId: z.string().min(1, 'Choose a role.'),
  needsMoneyLimit: z.boolean().optional(),
  modules: z.array(z.string()).optional(),
  maxAmount: z.coerce.number().optional(),
  currencyCode: z.string().optional(),
  scope: z.enum(['own', 'project', 'department', 'location', 'country', 'all']).optional(),
}).refine((data) => !data.needsMoneyLimit || (data.modules && data.modules.length > 0), { message: 'Choose at least one thing they can approve.', path: ['modules'] })
  .refine((data) => !data.needsMoneyLimit || (data.maxAmount && data.maxAmount > 0), { message: 'Enter an amount greater than zero.', path: ['maxAmount'] });
type CombinedFormValues = z.infer<typeof combinedFormSchema>;

/**
 * The real fix for "too many steps/pages to set someone up" - role
 * assignment and a money limit used to be two separate panels with
 * two separate add-forms the admin had to operate independently. This
 * is ONE guided action: pick a role, optionally say yes to "do they
 * need to approve money," and one button does both API calls and
 * shows one combined result. The tables further down the page are now
 * purely for reviewing/editing/removing what's already been granted -
 * this panel is the only place new access gets added.
 *
 * Modules are multi-select (real gap closed): someone who needs to
 * approve, say, both Leave and Training at the same amount previously
 * had to repeat the whole "Give Access" flow once per module. One
 * submission now creates one approval-limit row per selected module,
 * all sharing the same amount/currency/scope.
 */
function GiveAccessPanel({ userId, availableRoles, canManageUsers, canManageApprovals }: { userId: number; availableRoles: { id: number; name: string; description: string | null }[]; canManageUsers: boolean; canManageApprovals: boolean }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<CombinedFormValues>({
    resolver: zodResolver(combinedFormSchema),
    defaultValues: { needsMoneyLimit: false, currencyCode: 'NGN', scope: 'own', modules: [] },
  });
  const needsMoneyLimit = watch('needsMoneyLimit');

  const giveAccessMutation = useMutation({
    mutationFn: async (values: CombinedFormValues) => {
      await userRolesApi.assign(userId, Number(values.roleId));
      if (values.needsMoneyLimit && values.modules && values.modules.length > 0 && values.maxAmount) {
        for (const module of values.modules) {
          await approvalLimitsApi.create(userId, {
            module, maxAmount: values.maxAmount, currencyCode: values.currencyCode || 'NGN', scope: values.scope || 'own',
          });
        }
      }
      return values.needsMoneyLimit ? (values.modules?.length ?? 0) : 0;
    },
    onSuccess: (moduleCount) => {
      showSuccess(moduleCount > 0 ? `Access given - role assigned and ${moduleCount} money limit${moduleCount > 1 ? 's' : ''} set.` : 'Access given - role assigned.');
      queryClient.invalidateQueries({ queryKey: ['user-roles', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-approval-limits', userId] });
      reset({ roleId: '', needsMoneyLimit: false, modules: [], maxAmount: undefined, currencyCode: 'NGN', scope: 'own' });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to give access - if a role was assigned or some limits were set before the error, check below before trying again.'),
  });

  if (!canManageUsers) return null;

  return (
    <div className="panel">
      <SectionHeader title="Give This Person Access" />
      <div className="panel-body">
        <p className="muted">One step: pick their role, and if they'll need to approve anything with a cost, set that up right here too - no need to come back to a separate page afterward.</p>
        <form onSubmit={handleSubmit((values) => giveAccessMutation.mutate(values))}>
          <label>
            1. What's their role?
            <select {...register('roleId')}>
              <option value="">Choose a role…</option>
              {availableRoles.map((r) => (
                <option key={r.id} value={r.id}>{r.name} - {r.description ?? 'no description'}</option>
              ))}
            </select>
            {errors.roleId && <span className="field-error">{errors.roleId.message}</span>}
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" {...register('needsMoneyLimit')} disabled={!canManageApprovals} />
            2. Do they need to approve anything with a cost attached?
          </label>
          {!canManageApprovals && <p className="muted">You don't have permission to set money limits - ask an administrator to add one separately if this role needs it.</p>}

          {needsMoneyLimit && canManageApprovals && (
            <>
              <label>
                What are they approving? (select all that apply)
                <div className="checkbox-list" style={{ maxHeight: 220, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px' }}>
                  {Object.entries(MODULE_LABELS).map(([value, label]) => (
                    <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 400 }}>
                      <input type="checkbox" value={value} {...register('modules')} />
                      {label}
                    </label>
                  ))}
                </div>
                {errors.modules && <span className="field-error">{errors.modules.message}</span>}
                <p className="muted" style={{ marginTop: 6 }}>
                  Note: "Leave requests" here means who can <em>approve</em> other people's leave. To set how many leave days <em>this person themselves</em> gets each year, go to HR &gt; Staff &gt; their profile &gt; Leave tab instead - that's a different setting.
                </p>
              </label>
              <label>
                Up to how much can they approve?
                <input type="number" step="0.01" {...register('maxAmount')} />
                {errors.maxAmount && <span className="field-error">{errors.maxAmount.message}</span>}
              </label>
              <label>
                Currency
                <input {...register('currencyCode')} />
              </label>
              <label>
                For which records?
                <select {...register('scope')}>
                  {scopes.map((s) => <option key={s} value={s}>{SCOPE_LABELS[s]}</option>)}
                </select>
              </label>
            </>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={giveAccessMutation.isPending}>{giveAccessMutation.isPending ? 'Giving Access…' : 'Give Access'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const id = Number(userId);
  const canManageUsers = usePermission('admin.users.manage');
  const canManageApprovals = usePermission('admin.approvals.manage');
  const canManageOverrides = usePermission('admin.permissions.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const userQuery = useQuery({ queryKey: ['user', id], queryFn: () => usersApi.get(id) });
  const docsQuery = useQuery({ queryKey: ['user-documents', id], queryFn: () => genericDocumentApi.listFor('user', id) });
  const allRolesQuery = useQuery({ queryKey: ['roles'], queryFn: () => rolesApi.list() });
  const userRolesQuery = useQuery({ queryKey: ['user-roles', id], queryFn: () => userRolesApi.list(id) });
  const overridesQuery = useQuery({ queryKey: ['user-overrides', id], queryFn: () => permissionOverridesApi.list(id) });

  const { register: registerOverride, handleSubmit: handleSubmitOverride, reset: resetOverride, formState: { errors: overrideErrors } } = useForm<OverrideFormValues>({
    resolver: zodResolver(overrideFormSchema),
    defaultValues: { scope: 'own', isGranted: 'true' },
  });

  const setOverrideMutation = useMutation({
    mutationFn: (values: OverrideFormValues) => permissionOverridesApi.set(id, { permissionKey: values.permissionKey, scope: values.scope, isGranted: values.isGranted === 'true' }),
    onSuccess: () => { showSuccess('Exception added.'); queryClient.invalidateQueries({ queryKey: ['user-overrides', id] }); resetOverride({ permissionKey: '', scope: 'own', isGranted: 'true' }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add exception.'),
  });

  const removeOverrideMutation = useMutation({
    mutationFn: (permissionKey: string) => permissionOverridesApi.remove(id, permissionKey),
    onSuccess: () => { showSuccess('Exception removed.'); queryClient.invalidateQueries({ queryKey: ['user-overrides', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove exception.'),
  });

  const handleRemoveOverride = (permissionKey: string) => {
    if (window.confirm(`Remove this exception for "${permissionKey}"? This person's access for it will fall back to whatever their roles alone allow.`)) {
      removeOverrideMutation.mutate(permissionKey);
    }
  };

  const removeRoleMutation = useMutation({
    mutationFn: (roleId: number) => userRolesApi.remove(id, roleId),
    onSuccess: () => {
      showSuccess('Role removed.');
      queryClient.invalidateQueries({ queryKey: ['user-roles', id] });
    },
    // Surfaces the last-admin-protection guard if this role is what
    // made this user the sole active admin.
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove role.'),
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const assignedRoleIds = new Set(userRolesQuery.data?.data.map((r) => r.id));
  const availableRoles = allRolesQuery.data?.data.filter((r) => r.is_active && !assignedRoleIds.has(r.id)) ?? [];

  return (
    <div className="page">
      <Link to="/admin/users">&larr; Back to Users</Link>

      {userQuery.data && (
        <PageHeader title={userQuery.data.data.full_name} description={userQuery.data.data.email} readOnly={!canManageUsers} />
      )}

      {availableRoles.length > 0 && (
        <GiveAccessPanel userId={id} availableRoles={availableRoles} canManageUsers={canManageUsers} canManageApprovals={canManageApprovals} />
      )}

      <div className="panel">
        <SectionHeader title="Current Access" />
        <div className="panel-body">
        <p className="muted">Roles this person already has. To change what a role allows, remove it and give access again above with the right one.</p>
        {userRolesQuery.data && (
          <DataTable
            rows={userRolesQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage='This person has no access yet - use "Give This Person Access" above to get started.'
            columns={[
              { header: 'Role', render: (r) => r.name },
              { header: 'What it allows', render: (r) => r.description ?? '-' },
              {
                header: '',
                render: (r) =>
                  canManageUsers ? (
                    <IconActionButton icon={Trash2} label="Remove" danger onClick={() => removeRoleMutation.mutate(r.id)} />
                  ) : null,
              },
            ]}
          />
        )}
        </div>
      </div>

      <CommentsThread entityType="user" entityId={id} section="roles" heading="Comments for Roles" />

      <ApprovalLimitsSection userId={id} canManage={canManageApprovals} />

      <CommentsThread entityType="user" entityId={id} section="approval_limits" heading="Comments for Approval Limits" />

      <div className="panel">
        <SectionHeader
          title="Advanced: One-Off Exceptions"
          actions={<button className="btn-secondary" onClick={() => setShowAdvanced((v) => !v)}>{showAdvanced ? 'Hide' : 'Show'}</button>}
        />
        {showAdvanced && (
          <div className="panel-body">
          <p className="muted">Only use this for a rare, specific exception that doesn't fit any role above - for example, granting or blocking one single permission for just this person. Most people should never need this section.</p>
          {overridesQuery.data && (
            <DataTable
              rows={overridesQuery.data.data}
              rowKey={(o) => o.id}
              emptyMessage="No exceptions set - this person's access comes entirely from their roles above, which is the normal, recommended setup."
              columns={[
                { header: 'Permission', render: (o) => o.permission_key },
                { header: 'Applies to', render: (o) => SCOPE_LABELS[o.scope as PermissionScope] ?? o.scope },
                { header: 'Effect', render: (o) => (o.is_granted ? 'Extra access granted' : 'Access specifically blocked') },
                {
                  header: '',
                  render: (o) => canManageOverrides && (
                    <IconActionButton icon={Trash2} label="Remove" danger disabled={removeOverrideMutation.isPending} onClick={() => handleRemoveOverride(o.permission_key)} />
                  ),
                },
              ]}
            />
          )}

          {canManageOverrides && (
            <form className="inline-form" onSubmit={handleSubmitOverride((values) => setOverrideMutation.mutate(values))}>
              <label>
                Permission key
                <input placeholder="e.g. admin.departments.manage" {...registerOverride('permissionKey')} />
                {overrideErrors.permissionKey && <span className="field-error">{overrideErrors.permissionKey.message}</span>}
              </label>
              <label>
                Applies to
                <select {...registerOverride('scope')}>
                  {scopes.map((s) => <option key={s} value={s}>{SCOPE_LABELS[s]}</option>)}
                </select>
              </label>
              <label>
                Effect
                <select {...registerOverride('isGranted')}>
                  <option value="true">Grant extra access</option>
                  <option value="false">Block access</option>
                </select>
              </label>
              <button type="submit" disabled={setOverrideMutation.isPending}>{setOverrideMutation.isPending ? 'Saving…' : 'Add Exception'}</button>
            </form>
          )}
          </div>
        )}
      </div>

      {showAdvanced && <CommentsThread entityType="user" entityId={id} section="permission_overrides" heading="Comments for Permission Overrides" />}

      <DocumentsSection
        documents={docsQuery.data?.data ?? []}
        downloadUrl={(documentId) => genericDocumentApi.downloadUrl('user', documentId)}
        upload={(input) => genericDocumentApi.uploadFor('user', id, input)}
        onDelete={(documentId) => genericDocumentApi.deleteFor('user', documentId)}
        queryKeyToInvalidate={['user-documents', id]}
        hrCategories={false}
      />

      <AuditHistoryTab entityType="user" entityId={id} />
    </div>
  );
}

const limitFormSchema = z.object({
  module: z.string().min(2, 'Enter a module name, e.g. general'),
  maxAmount: z.coerce.number().positive('Must be greater than zero.'),
  currencyCode: z.string().length(3, 'Use a 3-letter currency code, e.g. NGN'),
  scope: z.enum(['own', 'project', 'department', 'location', 'country', 'all']),
});
type LimitFormValues = z.infer<typeof limitFormSchema>;

function ApprovalLimitsSection({ userId, canManage }: { userId: number; canManage: boolean }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [editingLimit, setEditingLimit] = useState<{ id: number; module: string } | null>(null);

  const limitsQuery = useQuery({ queryKey: ['user-approval-limits', userId], queryFn: () => approvalLimitsApi.list(userId) });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<LimitFormValues>({
    resolver: zodResolver(limitFormSchema),
    defaultValues: { currencyCode: 'NGN', scope: 'own' },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['user-approval-limits', userId] });

  const updateMutation = useMutation({
    mutationFn: (values: LimitFormValues) => approvalLimitsApi.update(editingLimit!.id, values),
    onSuccess: () => { showSuccess('Approval limit updated.'); invalidate(); reset(); setEditingLimit(null); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update approval limit.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (limitId: number) => approvalLimitsApi.delete(limitId),
    onSuccess: () => { showSuccess('Approval limit removed.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove approval limit.'),
  });

  const openEdit = (limit: ApprovalLimit) => {
    setEditingLimit({ id: limit.id, module: limit.module });
    reset({ module: limit.module, maxAmount: Number(limit.max_amount), currencyCode: limit.currency_code, scope: limit.scope });
  };
  const handleDelete = (limit: ApprovalLimit) => {
    if (window.confirm(`Remove this person's approval limit for "${MODULE_LABELS[limit.module] ?? limit.module}"? They will no longer be able to approve anything in this area until a new limit is set.`)) {
      deleteMutation.mutate(limit.id);
    }
  };

  return (
    <div className="panel">
      <SectionHeader title="Current Money Limits" />
      <div className="panel-body">
      <p className="muted">To give someone a new money limit, use "Give This Person Access" above. This section is for reviewing or removing limits they already have.</p>

      {editingLimit && (
        <form className="inline-form" onSubmit={handleSubmit((values) => updateMutation.mutate(values))}>
          <p className="muted">Editing the limit for "{MODULE_LABELS[editingLimit.module] ?? editingLimit.module}". To change what this covers, remove it below and give access again instead.</p>
          <label>
            Up to how much can they approve?
            <input type="number" step="0.01" {...register('maxAmount')} />
            {errors.maxAmount && <span className="field-error">{errors.maxAmount.message}</span>}
          </label>
          <label>
            Currency
            <input {...register('currencyCode')} />
          </label>
          <label>
            For which records?
            <select {...register('scope')}>
              {scopes.map((s) => <option key={s} value={s}>{SCOPE_LABELS[s]}</option>)}
            </select>
          </label>
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>{updateMutation.isPending ? 'Saving…' : 'Save Changes'}</button>
            <button type="button" className="btn-secondary" onClick={() => setEditingLimit(null)}>Cancel</button>
          </div>
        </form>
      )}

      {limitsQuery.data && (
        <DataTable
          rows={limitsQuery.data.data}
          rowKey={(l) => l.id}
          emptyMessage="No money limits set yet."
          columns={[
            { header: 'Can approve', render: (l) => MODULE_LABELS[l.module] ?? l.module },
            { header: 'Up to', render: (l) => `${l.currency_code} ${Number(l.max_amount).toLocaleString()}` },
            { header: 'For which records', render: (l) => SCOPE_LABELS[l.scope as PermissionScope] ?? l.scope },
            { header: 'Status', render: (l) => (l.is_active ? 'Active' : 'Inactive') },
            {
              header: '',
              render: (l) => canManage && (
                <span className="icon-action-group">
                  <IconActionButton icon={Pencil} label="Review" onClick={() => openEdit(l)} />
                  <IconActionButton icon={Trash2} label="Delete" danger disabled={deleteMutation.isPending} onClick={() => handleDelete(l)} />
                </span>
              ),
            },
          ]}
        />
      )}
      </div>
    </div>
  );
}
